/**
 * Preset Analytics Component
 * Shows user's preset performance stats and community engagement
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Download,
  Play,
  DollarSign,
  Share2,
  Star,
  BarChart3,
  Award,
  Users,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat card component
const StatCard = ({ icon: Icon, label, value, subtext, color = 'violet', trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-zinc-900/50 border border-${color}-500/20 rounded-xl p-4`}
  >
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      {trend && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
          trend > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-zinc-500">{label}</p>
      {subtext && <p className="text-[10px] text-zinc-600 mt-1">{subtext}</p>}
    </div>
  </motion.div>
);

// Top preset item
const TopPresetItem = ({ preset, rank }) => (
  <div className="flex items-center gap-3 py-2 border-b border-zinc-800 last:border-0">
    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
      rank === 1 ? 'bg-amber-500/20 text-amber-400' :
      rank === 2 ? 'bg-zinc-400/20 text-zinc-400' :
      rank === 3 ? 'bg-orange-500/20 text-orange-400' :
      'bg-zinc-800 text-zinc-500'
    }`}>
      {rank}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-white truncate">{preset.name}</p>
      <div className="flex items-center gap-3 mt-0.5">
        <span className="text-[10px] text-zinc-500">
          <Download className="w-3 h-3 inline mr-1" />
          {preset.downloads || 0}
        </span>
        <span className="text-[10px] text-zinc-500">
          <Play className="w-3 h-3 inline mr-1" />
          {preset.plays || 0}
        </span>
      </div>
    </div>
    <span className="text-lg">{preset.icon}</span>
  </div>
);

// Trending preset from community
const TrendingPresetItem = ({ preset, onPreview }) => (
  <button
    onClick={() => onPreview?.(preset)}
    className="flex items-center gap-3 p-3 bg-zinc-900/30 border border-zinc-800 rounded-lg hover:border-violet-500/30 transition-colors text-left w-full"
  >
    <span className="text-2xl">{preset.icon}</span>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-white truncate">{preset.name}</p>
      <p className="text-[10px] text-zinc-500">by {preset.creator?.name || 'Unknown'}</p>
    </div>
    <div className="text-right">
      <p className="text-xs text-violet-400">{preset.downloads || 0} downloads</p>
      <div className="flex items-center gap-0.5 justify-end">
        {[1,2,3,4,5].map(i => (
          <Star key={i} className={`w-2.5 h-2.5 ${
            i <= Math.round(preset.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-zinc-600'
          }`} />
        ))}
      </div>
    </div>
  </button>
);

/**
 * Main Preset Analytics Component
 */
export const PresetAnalytics = ({ userId, onPreviewPreset }) => {
  const [analytics, setAnalytics] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);
  const [trendingPresets, setTrendingPresets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch user analytics
        if (userId) {
          const userRes = await fetch(`${API_URL}/api/guitar-presets/analytics/user?user_id=${userId}`);
          if (userRes.ok) {
            const data = await userRes.json();
            setAnalytics(data);
          }
        }

        // Fetch global stats
        const globalRes = await fetch(`${API_URL}/api/guitar-presets/analytics/global`);
        if (globalRes.ok) {
          const data = await globalRes.json();
          setGlobalStats(data);
        }

        // Fetch trending presets
        const trendingRes = await fetch(`${API_URL}/api/guitar-presets/community/trending?limit=5`);
        if (trendingRes.ok) {
          const data = await trendingRes.json();
          setTrendingPresets(data.presets || []);
        }
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Your Preset Stats */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-violet-400" />
          Your Preset Performance
        </h3>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={Share2}
            label="Shared Presets"
            value={analytics?.total_shared || 0}
            subtext={`of ${analytics?.total_presets || 0} total`}
            color="violet"
          />
          <StatCard
            icon={Download}
            label="Total Downloads"
            value={analytics?.total_downloads || 0}
            color="cyan"
          />
          <StatCard
            icon={Play}
            label="Total Plays"
            value={analytics?.total_plays || 0}
            color="emerald"
          />
          <StatCard
            icon={DollarSign}
            label="Earnings"
            value={`$${(analytics?.total_earnings || 0).toFixed(2)}`}
            subtext="70% creator share"
            color="amber"
          />
        </div>
      </div>

      {/* Top Performing Presets */}
      {analytics?.top_presets?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-400" />
            Your Top Presets
          </h3>
          <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-3">
            {analytics.top_presets.map((preset, index) => (
              <TopPresetItem key={preset.id} preset={preset} rank={index + 1} />
            ))}
          </div>
        </div>
      )}

      {/* Community Stats */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-pink-400" />
          Community Stats
        </h3>
        
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-4 text-center">
            <p className="text-xl font-bold text-white">{globalStats?.total_shared_presets || 0}</p>
            <p className="text-[10px] text-zinc-500">Shared Presets</p>
          </div>
          <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-4 text-center">
            <p className="text-xl font-bold text-white">{globalStats?.total_downloads || 0}</p>
            <p className="text-[10px] text-zinc-500">Total Downloads</p>
          </div>
          <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-4 text-center">
            <p className="text-xl font-bold text-white">{globalStats?.total_plays || 0}</p>
            <p className="text-[10px] text-zinc-500">Total Plays</p>
          </div>
        </div>

        {/* Top Categories */}
        {globalStats?.top_categories?.length > 0 && (
          <div className="mt-3 bg-zinc-900/30 border border-zinc-800 rounded-xl p-4">
            <p className="text-xs text-zinc-400 mb-2">Popular Categories</p>
            <div className="flex flex-wrap gap-2">
              {globalStats.top_categories.map((cat, index) => (
                <span
                  key={cat.category}
                  className="px-2 py-1 rounded-full text-[10px] bg-zinc-800 text-zinc-300"
                >
                  {cat.category} ({cat.count})
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Trending Presets */}
      {trendingPresets.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            Trending in Community
          </h3>
          <div className="space-y-2">
            {trendingPresets.map(preset => (
              <TrendingPresetItem
                key={preset.id}
                preset={preset}
                onPreview={onPreviewPreset}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PresetAnalytics;
